import React, {
    useEffect,
    useReducer,
} from 'react';
import './PianoRoll.css';
import {
    ContainerAtom,
    HorizontalNoteContainer,
    NoteRangeType
} from '../../model/containers';
import { getNewNoteDurationForInsert } from '../../render/renderUtils';
import _ from 'lodash';
import {
    __DEFAULT_NOTE_RANGES,
    Note,
    NoteDuration,
    NoteVelocity,
} from '../../model/core';
import { RollRedrawObserver } from '../../utils/RollRedrawObserver';

import {
    CaretController,
    ScrollController
} from '../../utils/ScrollController';

import {
    AudioSource,
    getDefaulAudioSource,
} from '../../audio/routing';
import { NoteActivityObserver } from '../../audio/NoteActivity';
import { PianoRollKeybed } from './PianoKeybed';
import { NoteGlyph } from './NoteGlyph';
import { NoteSelectionGroup } from '../../utils/NoteSelectionGroup';
import {
    VisualSelectionHandlerFactory
} from './VisualSelection';
import {
    createHarmonicPredicates,
} from '../../utils/harmonyHighlight';

import { RydInstrument } from '../../model/instrument';
import { PianoRollTrack } from '../layouts/PianoRollTrack';
import {
    ComputedContextFrame,
} from '../../model/context';
import {
    RydTrack,
    RydTrackUIState
} from '../../model/tracks';
import { useWorkspaceUIState } from '../../stores/WorkspaceStore';
import { TrackColor } from '../../render/trackColor';
import { RydInputSource } from '../../audio/input';
import { RydProject } from '../../model/project';


export type VisualSelection = {
    factory?: VisualSelectionHandlerFactory,
    setPosition: (pos?: { startX: number, startY: number, endX: number, endY: number }) => void,
    setSelectionActive: (active: boolean) => void,
    active: boolean
};

function PianoRollNoteSet({
    hnc,
    trackId,
    noteRange,
    readonly,
    fixedDuration,
    updateHNCCB,
    scrollController,
    trackColor = TrackColor.DEFAULT_COLOR,
    normalizeDropPosition = (x: number, zoomFactor: number) => x,
}: {
    hnc: HorizontalNoteContainer,
    trackId: number,
    noteRange: NoteRangeType,
    readonly: boolean,
    fixedDuration?: boolean,
    updateHNCCB: (hnc: HorizontalNoteContainer) => void,
    scrollController?: ScrollController,
    trackColor?: TrackColor.Color,
    normalizeDropPosition?: (x: number, zoomFactor: number) => number,
}) {

    const updateNoteDuration = (
        start: number,
        original: ContainerAtom<Note>,
        delta: number
    ) => {

        if (readonly) return;
        hnc.remove(start, original);

        const duration = original.duration.value + delta;
        if (duration > 0) {
            hnc.remove(start, original);
            hnc.put(start, { ...original, duration: new NoteDuration(duration) });
        }

        updateHNCCB(hnc);
    };

    const moveNote = (
        start: number,
        note: ContainerAtom<Note>,
        deltaX: number,
        deltaY: number,
    ) => {
        const newStart = start + deltaX;
        if (newStart < 0) return;
        const newNoteIndex = note.payload.index - deltaY;
        if (newNoteIndex < noteRange.min || newNoteIndex >= noteRange.max) return;
        const newNote = Note.fromIndex(newNoteIndex);
        hnc.remove(start, note);
        hnc.put(newStart, {...note, payload: newNote});
        updateHNCCB(hnc);
    };

    const removeNote = (start: number, note: ContainerAtom<Note>) => {
        hnc.remove(start, note);
        updateHNCCB(hnc);
    };

    return (
        <>
            {hnc.getGrid().map((ng, i) => !ng ? null : ng.map((n, j) =>
                <NoteGlyph key={(i * (noteRange.max - noteRange.min)) + j}
                    trackId={trackId}
                    noteRange={noteRange}
                    start={i}
                    note={n}
                    duration={n.duration}
                    updateNoteDurationCB={_.curry(updateNoteDuration)(i)(n)}
                    moveNote={moveNote}
                    removeNoteCB={removeNote}
                    fixedDuration={fixedDuration}
                    updateHNC={updateHNCCB}
                    trackColor={trackColor}
                    normalizeDropPosition={normalizeDropPosition}
                />
            ))}
        </>
    );
}

export type PianoRollConfig = {
    name: string,
    showSheet: boolean,
    readonly: boolean
};

export type PianoRollDisplayMode = 'standalone'
    | 'track';

function PianoRoll({
    project,
    hnc,
    track,
    setTrack,
    contextFrame,
    uiState = {
        trackId: 1,
        collapsed: false,
        noteRange: __DEFAULT_NOTE_RANGES.notes,
        tonalityVisible: false,
        color: TrackColor.DEFAULT_COLOR,
    },
    setTrackUIState,
    readonly,
    caretController,
    redrawObserver,
    trackInstrument,
    setTrackInstrument,
    inputSource = getDefaulAudioSource(),
    setInputSource,
    mode = 'standalone'
}: {
    project: RydProject,
    hnc: HorizontalNoteContainer,
    track: RydTrack,
    setTrack: (track: RydTrack) => void,
    contextFrame: ComputedContextFrame,
    uiState?: RydTrackUIState,
    setTrackUIState: (uiState: RydTrackUIState) => void,
    readonly: boolean,
    caretController?: CaretController,
    redrawObserver?: RollRedrawObserver,
    trackInstrument?: RydInstrument,
    setTrackInstrument?: (instrument: RydInstrument) => void,
    inputSource? : RydInputSource,
    setInputSource?: (source: RydInputSource) => void,
    mode?: PianoRollDisplayMode
}) {

    const [notes, setNotes] = React.useState(hnc);
    const [, forceUpdate] = useReducer(x => x + 1, 0);
    const [selectionGroup, setSelectionGroup] = React.useState<NoteSelectionGroup>();
    const [visualSelectionActive, setVisualSelectionActive] = React.useState(false);
    const [tonalityHighlightVisible, setTonalityHighlightVisible] = React.useState(false);
    const workspaceUiState = useWorkspaceUIState();

    const [config, setConfig] = React.useState<PianoRollConfig>({
        name: track?.name || "Untitled",
        showSheet: false,
        readonly: readonly ? readonly : false
    });

    const addNoteToContainer = (
        readonly: boolean,
        start: number,
        note: Note,
        velocity: NoteVelocity = 100
    ) => {
        if (readonly) return;

        const payload : ContainerAtom<Note>= {
            step: start,
            payload: note,
            duration: getNewNoteDurationForInsert(workspaceUiState.newNoteDuration, hnc.getPPQ()),
            velocity: velocity
        };
        hnc.put(start, payload);
        setNotes(hnc);
        // FIXME: this is a hack to force a re-render when the grid is updated (deep object comparison is not working)
        forceUpdate();
    };

    const updateHNC = (hnc: HorizontalNoteContainer) => {
        setNotes(hnc);
        forceUpdate();
    };

    const noteActivityObserver = NoteActivityObserver.getInstance();

    useEffect(() => {
        redrawObserver?.addObserver(forceUpdate);
        return () => {
            redrawObserver?.dropObserver(forceUpdate);
        };
    }, []);

    const harmonicPrecidates = createHarmonicPredicates(tonalityHighlightVisible ? contextFrame.tonality : undefined);

    switch (mode) {
        case 'track':
            return (<PianoRollTrack
                project={project}
                hnc={hnc}
                track={track}
                setTrack={setTrack}
                uiState={uiState}
                setTrackUIState={setTrackUIState}
                readonly={readonly}
                scrollController={caretController?.scrollController}
                trackInstrument={trackInstrument}
                setTrackInstrument={setTrackInstrument}
                inputSource={inputSource}
                setInputSource={setInputSource}
                config={config}
                setConfig={setConfig}
                addNoteToContainer={addNoteToContainer}
                harmonicPrecidates={harmonicPrecidates}
                selectionGroup={selectionGroup}
                setSelectionGroup={setSelectionGroup}
                updateHNC={updateHNC}
                visualSelectionActive={visualSelectionActive}
                setVisualSelectionActive={setVisualSelectionActive}
                tonalityHighlightVisible={tonalityHighlightVisible}
                setTonalityHighlightVisible={setTonalityHighlightVisible}
                caretController={caretController}
                noteActivityObserver={noteActivityObserver}
            />);
        case 'standalone':
            // return (<PianoRollStandalone
            //     hnc={hnc}
            //     notes={notes}
            //     contextFrame={contextFrame}
            //     uiState={uiState}
            //     setTrackUIState={setTrackUIState}
            //     readonly={readonly}
            //     scrollController={caretController?.scrollController}
            //     // setContextFrame={setContextFrame}
            //     trackInstrument={trackInstrument}
            //     setTrackInstrument={setTrackInstrument}
            //     midiSource={midiSource}
            //     setMidiSource={setMidiSource}
            //     // setProjectCB={setProjectCB}
            //     config={config}
            //     setConfig={setConfig}
            //     newNoteDuration={newNoteDuration}
            //     updatePianoRoll={updatePianoRoll}
            //     harmonicPrecidates={harmonicPrecidates}
            //     selectionGroup={selectionGroup}
            //     setSelectionGroup={setSelectionGroup}
            //     updateHNC={updateHNC}
            //     factory={factory}
            //     visualSelectionActive={visualSelectionActive}
            //     setVisualSelectionActive={setVisualSelectionActive}
            //     tonalityHighlightVisible={tonalityHighlightVisible}
            //     setTonalityHighlightVisible={setTonalityHighlightVisible}
            //     caretController={caretController}
            //     setNewNoteDuration={setNewNoteDuration}
            //     noteActivityObserver={noteActivityObserver}
            // />);
            return null;
        default:
            throw new Error(`Unknown mode: ${mode}`);
    }
}

export default PianoRoll;
export {
    PianoRoll,
    PianoRollKeybed,
    PianoRollNoteSet,
};

